import tokml from 'tokml/tokml.js';
import { Feature, FeatureCollection } from 'geojson';
import { featureCollection, getType } from '@turf/turf';
import { useContext, useMemo } from 'react';
import { MapContext } from '../../../contexts/MapContext';
import { geometryTypes, UNKNOWN_CROP_NAME } from '../../../constants/fields';
import {
  cropColorsMap,
  cropThemes,
  defaultCropColorForTheme,
} from '../../../utils/colors';
import { editPointStyles, TO_KML_OPTIONS } from '../../../utils/kml';
import ExportSectionFeaturesButton, {
  ExportSectionFeaturesButtonProps,
} from './ExportSectionFeaturesButton';

// Non-exhaustive set of crop properties
interface Crop {
  id: string;
  name: string;
}

// Non-exhaustive set of section properties
interface Section {
  crop?: Crop;
  id: string;
}

// Determines the color of the given feature based on the feature cropName or
// the primary crop of the given section
const getFeatureColor = (
  feature: Feature,
  section: Section,
  colorsMap: { [cropName: string]: string },
) => {
  const { properties } = feature;
  const cropName =
    properties?.cropName || section.crop?.name || UNKNOWN_CROP_NAME;
  const color = colorsMap[cropName];
  return color || defaultCropColorForTheme(cropThemes.LIGHT); // defaults to Unknown
};

const getFeatureStyles = (
  feature: Feature,
  section: Section,
  colorsMap: { [cropName: string]: string },
) => {
  const color = getFeatureColor(feature, section, colorsMap);
  return getType(feature) === geometryTypes.POINT
    ? // Apply marker color only for points
      { 'marker-color': color }
    : {
        fill: '#000',
        'fill-opacity': 0.35,
        stroke: color,
        'stroke-width': 2,
      };
};

const ExportSectionKmlButton = (props: ExportSectionFeaturesButtonProps) => {
  const {
    // @ts-expect-error MapContext not migrated to TS
    crops: { availableCrops },
  } = useContext(MapContext);
  const colorsMap = useMemo(
    () => cropColorsMap(Object.values(availableCrops), cropThemes.LIGHT),
    [availableCrops],
  );

  const transformFeatures = (
    featureColl: FeatureCollection,
    section: Section,
  ) => {
    // Apply styles to all features
    const styledData = featureCollection(
      featureColl.features.map((feature: Feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          // @ts-expect-error colors.js not migrated to TS
          ...getFeatureStyles(feature, section, colorsMap),
        },
      })),
    );
    // Convert GeoJSON to KML
    const kmlData = tokml(styledData, TO_KML_OPTIONS);
    // Fix broken KML Point styles
    return editPointStyles(kmlData);
  };

  return (
    <ExportSectionFeaturesButton
      {...props}
      contentType="text/xml"
      extension="kml"
      transformFeatures={transformFeatures}
      zipTitle="KML"
    />
  );
};

export default ExportSectionKmlButton;
